// TODO: Overhaul — delete this file (and its consumers/tests) once the per-account transactions endpoint is
// wired (#432). See the note on `InvestmentAccountTransactionsQueriesService`.
import dayjs from 'dayjs';
import Decimal from 'decimal.js-light';
import { Client } from 'openapi-fetch';

import { AccountOf } from '@views/investor/queries/investment-accounts/investment-accounts.mock';
import { mockDelay as delay, ok, randomLatencyMs } from '@views/investor/queries/overhaul-mock.util';
import { TransactionTypes } from '@views/misc/enums/enums.helpers';

import {
  SquirrelMoneyCoreContractsInvestmentAccountTransactionDailySummaryDto as DailySummaryDto,
  SquirrelMoneyCoreContractsInvestmentAccountTransactionDto as InvestmentAccountTransactionDto,
  SquirrelMoneyCoreEnumerationsInvestmentAccountTypes as InvestmentAccountTypes,
  SquirrelMoneyContractsTypesInvestmentClasses as InvestmentClasses,
  SquirrelMoneyCoreContractsRateChangeDto as RateChangeDto,
  SquirrelMoneyCoreContractsInvestmentAccountTransactionSummaryDto as TransactionSummaryDto,
  SquirrelMoneyCoreEnumerationsTransactionTypes as TransactionType,
  paths as accountOverhaulPaths,
} from '@core/schemas/account-overhaul-schema';
import { components as api1Components, paths as api1Paths } from '@core/schemas/api1-schema';
import { paths as api2Paths } from '@core/schemas/api2-schema';
import { isFeatureEnabled } from '@core/utilities/feature-flags';

import { TransactionsDateRange } from './investment-account-transactions.query';

/** An API1 on-call bank-account row (`/v1/bank-accounts/{id}/transactionsByDate`): rate changes mixed in as type 0. */
export type Api1OnCallTransaction =
  api1Components['schemas']['Squirrel.Money.Core.Contracts.InvestmentAccountTransactionDto'];
/** An API1 investment with its transactions (`/v1/investment-transactions/{investmentId}/byDate`). */
export type Api1InvestmentActivity = api1Components['schemas']['Squirrel.Money.Core.Contracts.InvestmentActivityDto'];

const RATE_CHANGE: number = TransactionTypes.RateChange;

const dayKey = (date: string | undefined): string => dayjs(date).format('YYYY-MM-DD');
const byIdAsc = (a: InvestmentAccountTransactionDto, b: InvestmentAccountTransactionDto): number =>
  (a.investmentAccountTransactionId ?? 0) - (b.investmentAccountTransactionId ?? 0);

/**
 * An API1 rate change carries its rates only in the description: the first percentage is the old rate and
 * the last the new one (a lone percentage is the new rate). Undefined when there's no rate to read.
 */
function toRateChange(row: Api1OnCallTransaction): RateChangeDto | undefined {
  const rates = (row.description?.match(/\d+(?:\.\d+)?(?=\s*%)/g) ?? []).map(Number);
  if (rates.length === 0) return undefined;
  return {
    effectiveDateNzt: row.transactionDateNzt,
    oldRate: rates.length > 1 ? rates[0] : null,
    newRate: rates[rates.length - 1],
  };
}

/** API1 on-call rows → the account's transactions, with rate changes split out. */
export function fromOnCall(
  rows: readonly Api1OnCallTransaction[],
  investmentAccountId: number,
): { transactions: InvestmentAccountTransactionDto[]; rateChanges: RateChangeDto[] } {
  const transactions: InvestmentAccountTransactionDto[] = [];
  const rateChanges: RateChangeDto[] = [];
  for (const row of rows) {
    if (row.transactionTypeId === RATE_CHANGE) {
      const rateChange = toRateChange(row);
      if (rateChange) rateChanges.push(rateChange);
      continue;
    }
    transactions.push({
      investmentAccountTransactionId: row.bankAccountTransactionId,
      investmentAccountId,
      // The API1 payload has no created date; the bank date is the closest stand-in.
      createDateNzt: row.transactionDateNzt,
      transactionDateNzt: row.transactionDateNzt,
      availableDateNzt: row.availableDateNzt,
      amount: row.amount,
      closingBalance: row.closingBalance,
      description: row.description,
      investmentId: row.investmentId,
      interestOnly: row.interestOnly,
      isHidden: row.isHidden,
      transactionType: row.transactionTypeId,
    });
  }
  return { transactions, rateChanges };
}

/**
 * A Term Investment account's transactions, merged from the API1 transactions of each investment in its
 * class. The API1 rows only carry each investment's own balance, so the account's closing balance after a
 * transaction is the sum of every investment's latest balance at that point — an investment yet to transact
 * in the window counts at its first in-window balance (or its current balance when it has none).
 */
export function fromInvestments(
  activities: readonly Api1InvestmentActivity[],
  investmentAccountId: number,
): InvestmentAccountTransactionDto[] {
  const balances = new Map<number, number>();
  const transactions: (InvestmentAccountTransactionDto & { investmentBalance: number })[] = [];
  for (const activity of activities) {
    const rows = [...(activity.investmentTransactions?.items ?? [])]
      .filter(row => row.transactionTypeId !== RATE_CHANGE)
      .sort((a, b) => (a.investmentTransactionId ?? 0) - (b.investmentTransactionId ?? 0));
    balances.set(activity.investmentId ?? 0, rows[0]?.investmentClosingBalance ?? activity.closingBalance ?? 0);
    for (const row of rows) {
      transactions.push({
        investmentAccountTransactionId: row.investmentTransactionId,
        investmentAccountId,
        createDateNzt: row.transactionDateNzt,
        transactionDateNzt: row.transactionDateNzt,
        amount: row.amount,
        description: `${row.transactionTypeDisplay ?? 'Transaction'} — investment ${activity.investmentId}`,
        investmentId: activity.investmentId,
        interestOnly: row.interestOnly,
        isHidden: false,
        transactionType: row.transactionTypeId,
        investmentBalance: row.investmentClosingBalance ?? 0,
      });
    }
  }

  return transactions.sort(byIdAsc).map(({ investmentBalance, ...transaction }) => {
    balances.set(transaction.investmentId ?? 0, investmentBalance);
    const closingBalance = [...balances.values()].reduce((sum, b) => sum.add(b), new Decimal(0)).toNumber();
    return { ...transaction, closingBalance };
  });
}

function toDailySummary(dateNzt: string, transactions: InvestmentAccountTransactionDto[]): DailySummaryDto {
  const latest = [...transactions].sort(byIdAsc).at(-1);
  const byType = new Map<TransactionType, Decimal>();
  let dayTotal = new Decimal(0);
  for (const t of transactions) {
    const type = t.transactionType ?? TransactionType.Unknown;
    byType.set(type, (byType.get(type) ?? new Decimal(0)).add(t.amount ?? 0));
    dayTotal = dayTotal.add(t.amount ?? 0);
  }
  const closingBalance = latest?.closingBalance ?? 0;
  return {
    dateNzt,
    openingBalance: new Decimal(closingBalance).sub(dayTotal).toNumber(),
    closingBalance,
    summary: [...byType].map(([transactionType, amount]) => ({ transactionType, amount: amount.toNumber() })),
    transactions,
  };
}

/** Group an account's transactions into newest-first daily summaries by `createDateNzt` day. */
export function toTransactionSummary(
  transactions: readonly InvestmentAccountTransactionDto[],
  rateChanges: readonly RateChangeDto[],
  investmentAccountId: number,
  { startDate, endDate }: TransactionsDateRange,
): TransactionSummaryDto {
  const byDay = new Map<string, InvestmentAccountTransactionDto[]>();
  for (const transaction of transactions) {
    const day = dayKey(transaction.createDateNzt);
    byDay.set(day, [...(byDay.get(day) ?? []), transaction]);
  }
  const dailySummaries = [...byDay]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([day, dayTransactions]) => toDailySummary(`${day}T00:00:00`, dayTransactions));

  return {
    investmentAccountId,
    startDate,
    endDate,
    openingBalance: dailySummaries.at(-1)?.openingBalance,
    closingBalance: dailySummaries[0]?.closingBalance,
    dailySummaries,
    rateChanges: [...rateChanges],
  };
}

type AccountTransactions = { transactions: InvestmentAccountTransactionDto[]; rateChanges: RateChangeDto[] };

interface GetInit {
  params?: {
    path?: { investorEntityId?: number; investmentAccountId?: number };
    query?: Partial<TransactionsDateRange>;
  };
}

/**
 * A mock `ACCOUNT_OVERHAUL_CLIENT` fragment serving the per-account transactions endpoint from the live API1/API2
 * endpoints, split into accounts the way the accounts mock derives them: the Wallet reads the on-call bank
 * account; a Term Investment reads the transactions of every active investment in its class. Other accounts
 * (the interim on-call, managed fund, Legacy) have no API1 source, so they come back empty — as does
 * everything without the api clients (unit tests) or with the overhaul flag off.
 */
export function createInvestmentAccountTransactionsMockClient(
  deps: { api1?: Client<api1Paths>; api2?: Client<api2Paths>; accountOf?: AccountOf; delayMs?: number } = {},
): Client<accountOverhaulPaths> {
  const { api1, api2, accountOf, delayMs } = deps;
  const wait = () => delay(delayMs ?? randomLatencyMs());

  const onCallTransactions = async (
    entityId: number,
    accountId: number,
    range: TransactionsDateRange,
  ): Promise<AccountTransactions | null> => {
    if (!api1 || !api2) return null;
    const { data: onCall } = await api2.GET('/v2/on-call-accounts/{entityId}', {
      params: { path: { entityId }, query: { pageIndex: 0, pageSize: 1 } },
    });
    const bankAccountId = onCall?.bankAccount?.bankAccountId;
    if (bankAccountId == null) return null;
    const { data } = await api1.POST('/v1/bank-accounts/{bankAccountId}/transactionsByDate', {
      params: { path: { bankAccountId } },
      body: range,
    });
    return fromOnCall(data ?? [], accountId);
  };

  const classTransactions = async (
    entityId: number,
    accountId: number,
    investmentClass: InvestmentClasses,
    range: TransactionsDateRange,
  ): Promise<AccountTransactions | null> => {
    if (!api1) return null;
    const { data } = await api1.GET('/v1/investments/entities/{entityId}', {
      params: { path: { entityId }, query: { pageIndex: 1, pageSize: 500 } },
    });
    const endedBefore = (date: string | null | undefined) => date != null && dayKey(date) < range.startDate;
    const investmentIds = (data?.investments?.items ?? [])
      .filter(
        investment =>
          Number(investment.investmentClass) === Number(investmentClass) &&
          !endedBefore(investment.completedDateNzt) &&
          !endedBefore(investment.soldDateNzt),
      )
      .flatMap(investment => (investment.investmentId != null ? [investment.investmentId] : []));
    const activities = await Promise.all(
      investmentIds.map(async investmentId => {
        const { data: activity } = await api1.GET('/v1/investment-transactions/{investmentId}/byDate', {
          params: { path: { investmentId }, query: range },
        });
        return activity;
      }),
    );
    return {
      transactions: fromInvestments(
        activities.filter(activity => activity !== undefined),
        accountId,
      ),
      rateChanges: [],
    };
  };

  const sourceFor = async (
    entityId: number,
    accountId: number,
    range: TransactionsDateRange,
  ): Promise<AccountTransactions | null> => {
    if (!accountOf || !isFeatureEnabled('accountOverhaul')) return null;
    const account = await accountOf(entityId, accountId);
    if (account?.investmentAccountType === InvestmentAccountTypes.Wallet) {
      return onCallTransactions(entityId, accountId, range);
    }
    if (account?.investmentAccountType === InvestmentAccountTypes.TermInvestment && account.investmentClass != null) {
      return classTransactions(entityId, accountId, account.investmentClass, range);
    }
    return null;
  };

  return {
    async GET(_path: string, init?: GetInit) {
      const { investorEntityId, investmentAccountId = -1 } = init?.params?.path ?? {};
      const { startDate = '', endDate = '' } = init?.params?.query ?? {};
      const range = { startDate, endDate };
      const source = investorEntityId == null ? null : await sourceFor(investorEntityId, investmentAccountId, range);
      await wait();
      return ok(
        toTransactionSummary(source?.transactions ?? [], source?.rateChanges ?? [], investmentAccountId, range),
      );
    },
    // Cast: this stub implements only the transactions GET, not the whole overhaul surface.
  } as unknown as Client<accountOverhaulPaths>;
}
